// deps
import React, { memo, ReactElement, useContext, useEffect, useMemo } from "react";

// external-deps
import { DineoutCartContext } from "@swiggy-private/checkout-core";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { isEmpty } from "@swiggy-private/js-utils";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// hooks
import { useBillPaymentAnalytics } from "@dineout-cart-client/hooks/useDineoutCartAnalytics";

// constants
import { TOAST_ICON_DIMENSION, WIDE_TOAST_STYLE } from "@dineout-cart-client/constants";

// helpers
import { getDelayedActionToastWidget } from "@dineout-cart-client/helpers/cartHelper";

// styles
import { ToastContainer } from "@dineout-cart-client/components/table-reservation/styles";

/* The GDA ARD defines these impression context keys in snake_case. */
const PAY_WAITER_CALLOUT_KEYS = {
    tableId: "table_id",
    calloutText: "callout_text",
    restaurantId: "restaurant_id",
} as const;

export const DelayedActionToast = memo(function DelayedActionToast(): ReactElement | null {
    const { cartState } = useContext(DineoutCartContext);
    const { localCart, reviewedCart: wholeApiResponse } = cartState;
    const reviewedCart = wholeApiResponse?.data;
    const { onImpressionPayWaiterCallout } = useBillPaymentAnalytics();

    const delayedActionToastWidget = useMemo(
        () => getDelayedActionToastWidget(reviewedCart?.widgets),
        [reviewedCart?.widgets],
    );

    const delayedActionToastInfo = delayedActionToastWidget?.widgetInfo;

    const delayedActionToastIcon = useMemo(() => {
        if (isEmpty(delayedActionToastInfo?.actionIcon)) return undefined;
        return (
            <Image
                cloudinaryId={delayedActionToastInfo?.actionIcon}
                alt=""
                width={TOAST_ICON_DIMENSION}
                height={TOAST_ICON_DIMENSION}
            />
        );
    }, [delayedActionToastInfo?.actionIcon]);

    useEffect(() => {
        if (isEmpty(delayedActionToastInfo?.actionText)) return;
        onImpressionPayWaiterCallout({
            [PAY_WAITER_CALLOUT_KEYS.tableId]: localCart?.cartMetaData?.tableId || "",
            parentOrderId: localCart?.cartMetaData?.parentOrderId || "",
            [PAY_WAITER_CALLOUT_KEYS.calloutText]: delayedActionToastInfo?.actionText || "",
            [PAY_WAITER_CALLOUT_KEYS.restaurantId]: String(
                localCart?.cartMetaData?.restaurantId || reviewedCart?.storesInfo?.[0]?.id || "",
            ),
        });
    }, [
        delayedActionToastInfo?.actionText,
        localCart?.cartMetaData?.tableId,
        localCart?.cartMetaData?.parentOrderId,
        localCart?.cartMetaData?.restaurantId,
        reviewedCart?.storesInfo,
        onImpressionPayWaiterCallout,
    ]);

    if (isEmpty(delayedActionToastInfo?.actionText)) return null;

    return (
        <ToastContainer>
            <Toast
                message={delayedActionToastInfo?.actionText || ""}
                leftAsset={delayedActionToastIcon}
                background={delayedActionToastInfo?.backgroundColor as keyof IColors}
                textColor={delayedActionToastInfo?.textColor as keyof IColors}
                containerClass={"toast"}
                containerStyle={WIDE_TOAST_STYLE}
                show
                disableOutsideClick
                disableClickAnimation
                removeHardcodedColorMode
            />
        </ToastContainer>
    );
});
